import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { SelectField } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const useCatalog = (enabled = true) => {
  const [items, setItems] = useState([]);
  useEffect(() => { if (enabled) api.get("/sales/products", { params: { active: true, limit: 100 } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [enabled]);
  return items;
};

export const ItemsPanel = ({ opportunity, canEdit, onChanged }) => {
  const catalog = useCatalog(canEdit);
  const [rows, setRows] = useState(opportunity.items || []);
  const [pick, setPick] = useState("");
  useEffect(() => setRows(opportunity.items || []), [opportunity.items]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(opportunity.items || []);

  const add = () => {
    const p = catalog.find((x) => x.id === pick);
    if (!p || rows.some((r) => r.product_id === p.id)) return;
    setRows([...rows, { product_id: p.id, code: p.code, name: p.name, kind: p.kind, unit: p.unit, unit_price: p.base_price, quantity: 1 }]);
    setPick("");
  };
  const save = async () => {
    try { await api.put(`/sales/opportunities/${opportunity.id}/items`, { items: rows.map((r) => ({ product_id: r.product_id, quantity: Number(r.quantity) || 1 })) }); toast.success("Prodotti/servizi salvati"); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const total = rows.reduce((s, r) => s + (Number(r.quantity) || 0) * r.unit_price, 0);

  return (
    <div data-testid="opp-items-panel">
      {rows.length === 0 ? <p className="text-sm text-slate-500" data-testid="opp-items-empty">Nessun prodotto o servizio collegato.</p> : (
        <ul className="divide-y divide-slate-100">{rows.map((r, i) => (
          <li key={r.product_id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`opp-item-${r.code}`}>
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink">{r.name}</p><p className="text-xs text-slate-500">{r.code} · {r.kind} · {fmtCurrency(r.unit_price)} / {r.unit}</p></div>
            {canEdit ? <Input type="number" min="0.001" step="any" className="h-8 w-24" value={r.quantity} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} data-testid={`opp-item-qty-${r.code}`} />
              : <span className="text-sm text-slate-600">× {r.quantity}</span>}
            {canEdit && <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`opp-item-remove-${r.code}`}><Trash2 className="h-4 w-4" /></Button>}
          </li>))}</ul>
      )}
      {rows.length > 0 && <p className="mt-3 text-right text-sm text-slate-500">Valore indicativo a listino: <span className="font-semibold text-ink">{fmtCurrency(total)}</span></p>}
      {canEdit && <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <SelectField className="flex-1" label="Aggiungi dal catalogo" value={pick} onChange={setPick} options={catalog.filter((p) => !rows.some((r) => r.product_id === p.id)).map((p) => ({ id: p.id, name: `${p.code} · ${p.name}` }))} testId="opp-item-pick" />
        <Button variant="outline" onClick={add} disabled={!pick} className="gap-1" data-testid="opp-item-add"><Plus />Aggiungi</Button>
        <Button onClick={save} disabled={!dirty} data-testid="opp-items-save">Salva</Button>
      </div>}
    </div>
  );
};
